import type { PartialCrmMessages } from "./englishCrmMessages";

/**
 * Tradução pt-BR específica do Atomic CRM (FaroMarket).
 *
 * Estrutura espelhada de englishCrmMessages.ts. Strings ausentes recaem
 * para o catálogo em inglês via `allowMissing: true` no polyglotI18nProvider.
 *
 * Convenções:
 * - "Deal" / "Deals" → "Oportunidade" / "Oportunidades" (terminologia comercial pt-BR)
 * - "Sale" (CRM user) → "Usuário"
 * - "Sector" → "Setor"
 * - Placeholders `%{name}`, `%{date}`, `%{smart_count}` preservados
 * - Plurais react-admin: `Singular |||| Plural`
 */
export const ptBrCrmMessages: PartialCrmMessages = {
  resources: {
    companies: {
      name: "Empresa |||| Empresas",
      forcedCaseName: "Empresa",
      fields: {
        name: "Nome da empresa",
        website: "Site",
        linkedin_url: "LinkedIn",
        phone_number: "Telefone",
        created_at: "Criada em",
        nb_contacts: "Quantidade de contatos",
        revenue: "Faturamento",
        sector: "Setor",
        size: "Porte",
        tax_identifier: "CNPJ",
        address: "Endereço",
        city: "Cidade",
        zipcode: "CEP",
        state_abbr: "UF",
        country: "País",
        description: "Descrição",
        context_links: "Links de contexto",
        sales_id: "Responsável",
      },
      empty: {
        description: "Sua lista de empresas está vazia.",
        title: "Nenhuma empresa encontrada",
      },
      field_categories: {
        contact: "Contato",
        additional_info: "Informações adicionais",
        address: "Endereço",
        context: "Contexto",
      },
      action: {
        create: "Cadastrar empresa",
        edit: "Editar empresa",
        new: "Nova empresa",
        show: "Exibir empresa",
      },
      added_on: "Adicionada em %{date}",
      followed_by: "Acompanhada por %{name}",
      followed_by_you: "Acompanhada por você",
      no_contacts: "Sem contatos",
      nb_contacts: "%{smart_count} contato |||| %{smart_count} contatos",
      nb_deals: "%{smart_count} oportunidade |||| %{smart_count} oportunidades",
      sizes: {
        one_employee: "1 colaborador",
        two_to_nine_employees: "2-9 colaboradores",
        ten_to_forty_nine_employees: "10-49 colaboradores",
        fifty_to_two_hundred_forty_nine_employees: "50-249 colaboradores",
        two_hundred_fifty_or_more_employees: "250 ou mais colaboradores",
      },
      autocomplete: {
        create_error: "Ocorreu um erro ao cadastrar a empresa",
        create_item: "Cadastrar %{item}",
        create_label: "Comece a digitar para cadastrar uma nova empresa",
      },
      filters: {
        only_mine: "Apenas empresas que eu gerencio",
      },
    },
    contacts: {
      name: "Contato |||| Contatos",
      forcedCaseName: "Contato",
      field_categories: {
        background_info: "Histórico",
        identity: "Identificação",
        misc: "Diversos",
        personal_info: "Informações pessoais",
        position: "Cargo",
      },
      fields: {
        first_name: "Nome",
        last_name: "Sobrenome",
        last_seen: "Último contato",
        title: "Cargo",
        company_id: "Empresa",
        email_jsonb: "E-mails",
        email: "E-mail",
        phone_jsonb: "Telefones",
        phone_number: "Telefone",
        linkedin_url: "LinkedIn",
        background: "Histórico (bio, como se conheceram, etc)",
        has_newsletter: "Recebe newsletter",
        sales_id: "Responsável",
      },
      action: {
        add: "Adicionar contato",
        add_first: "Adicione seu primeiro contato",
        create: "Cadastrar contato",
        edit: "Editar contato",
        export_vcard: "Exportar como vCard",
        new: "Novo contato",
        show: "Exibir contato",
      },
      background: {
        last_activity_on: "Última atividade em %{date}",
        added_on: "Adicionado em %{date}",
        followed_by: "Acompanhado por %{name}",
        followed_by_you: "Acompanhado por você",
        status_none: "Nenhum",
      },
      position_at: "%{title} em",
      position_at_company: "%{title} em %{company}",
      empty: {
        description: "Sua lista de contatos está vazia.",
        title: "Nenhum contato encontrado",
      },
      import: {
        title: "Importar contatos",
        button: "Importar CSV",
        complete:
          "Importação de contatos concluída. %{importCount} importados, %{errorCount} com erro.",
        progress:
          "Importados %{importCount} / %{rowCount} contatos, %{errorCount} com erro.",
        error:
          "Falha ao importar este arquivo. Confira se enviou um CSV válido.",
        imported: "Importados",
        remaining_time: "Tempo estimado restante:",
        running:
          "Importação em andamento — não feche esta aba.",
        sample_download: "Baixar CSV de exemplo",
        sample_hint: "Aqui está um CSV de exemplo para usar como modelo",
        stop: "Interromper importação",
        csv_file: "Arquivo CSV",
        contacts_label: "contato |||| contatos",
      },
      inputs: {
        genders: {
          male: "Ele/Dele",
          female: "Ela/Dela",
          nonbinary: "Elu/Delu",
        },
        personal_info_types: {
          work: "Trabalho",
          home: "Pessoal",
          other: "Outro",
        },
      },
      list: {
        error_loading: "Erro ao carregar os contatos",
      },
      bulk_tag: {
        action: "Etiquetar",
        back: "Voltar para etiquetas",
        create_description:
          "Crie uma nova etiqueta e aplique aos contatos selecionados.",
        description:
          "Escolha uma etiqueta existente ou crie uma nova para os contatos selecionados.",
        empty:
          "Nenhuma etiqueta ainda. Crie uma para etiquetar os contatos selecionados.",
        error: "Falha ao adicionar etiqueta aos contatos",
        noop: "Os contatos selecionados já têm essa etiqueta",
        success:
          "Etiqueta aplicada a %{smart_count} contato |||| Etiqueta aplicada a %{smart_count} contatos",
        title: "Adicionar etiqueta aos contatos",
      },
      merge: {
        action: "Mesclar com outro contato",
        confirm: "Mesclar contatos",
        current_contact: "Contato atual (será excluído)",
        description: "Mescle este contato com outro.",
        error: "Falha ao mesclar contatos",
        merging: "Mesclando...",
        no_additional_data: "Sem dados adicionais para mesclar",
        select_target: "Selecione um contato para mesclar",
        success: "Contatos mesclados com sucesso",
        target_contact: "Contato destino (será preservado)",
        title: "Mesclar contato",
        warning_description:
          "Todos os dados serão transferidos para o segundo contato. Essa ação não pode ser desfeita.",
        warning_title: "Atenção: Operação destrutiva",
        what_will_be_merged: "O que será mesclado:",
      },
      filters: {
        before_last_month: "Antes do mês passado",
        before_this_month: "Antes deste mês",
        before_this_week: "Antes desta semana",
        managed_by_me: "Sob minha gestão",
        search: "Buscar nome, empresa...",
        this_week: "Esta semana",
        today: "Hoje",
        tags: "Etiquetas",
        tasks: "Tarefas",
      },
      hot: {
        empty_change_status:
          'Mude o status de um contato adicionando uma anotação a ele e clicando em "exibir opções".',
        empty_hint: 'Contatos com status "quente" aparecem aqui.',
        title: "Contatos quentes",
      },
    },
    deals: {
      name: "Oportunidade |||| Oportunidades",
      fields: {
        name: "Nome",
        description: "Descrição",
        company_id: "Empresa",
        contact_ids: "Contatos",
        category: "Categoria",
        amount: "Valor",
        expected_closing_date: "Previsão de fechamento",
        stage: "Etapa",
      },
      action: {
        back_to_deal: "Voltar para a oportunidade",
        create: "Cadastrar oportunidade",
        new: "Nova oportunidade",
      },
      field_categories: {
        misc: "Diversos",
      },
      archived: {
        action: "Arquivar",
        error: "Erro: oportunidade não foi arquivada",
        list_title: "Oportunidades arquivadas",
        success: "Oportunidade arquivada",
        title: "Oportunidade arquivada",
        view: "Ver oportunidades arquivadas",
      },
      inputs: {
        linked_to: "Vinculada a",
      },
      unarchived: {
        action: "Voltar para o quadro",
        error: "Erro: oportunidade não foi desarquivada",
        success: "Oportunidade desarquivada",
      },
      updated: "Oportunidade atualizada",
      empty: {
        before_create: "antes de cadastrar uma oportunidade.",
        description: "Sua lista de oportunidades está vazia.",
        title: "Nenhuma oportunidade encontrada",
      },
      invalid_date: "Data inválida",
    },
    notes: {
      name: "Anotação |||| Anotações",
      forcedCaseName: "Anotação",
      fields: {
        status: "Status",
        date: "Data",
        attachments: "Anexos",
        contact_id: "Contato",
        deal_id: "Oportunidade",
      },
      action: {
        add: "Adicionar anotação",
        add_first: "Adicione sua primeira anotação",
        delete: "Excluir anotação",
        edit: "Editar anotação",
        update: "Atualizar anotação",
        add_this: "Adicionar esta anotação",
      },
      sheet: {
        create: "Criar anotação",
        create_for: "Criar anotação para %{name}",
        edit: "Editar anotação",
        edit_for: "Editar anotação de %{name}",
      },
      deleted: "Anotação excluída",
      empty: "Nenhuma anotação ainda",
      author_added: "%{name} adicionou uma anotação",
      you_added: "Você adicionou uma anotação",
      me: "Eu",
      list: {
        error_loading: "Erro ao carregar as anotações",
      },
      note_for_contact: "Anotação para %{name}",
      stepper: {
        hint: "Vá até a página de um contato e adicione uma anotação",
      },
      added: "Anotação adicionada",
      inputs: {
        add_note: "Adicionar uma anotação",
        options_hint: "(anexar arquivos ou alterar detalhes)",
        show_options: "Exibir opções",
      },
      actions: {
        attach_document: "Anexar documento",
      },
      validation: {
        note_or_attachment_required:
          "É necessário ter uma anotação ou um anexo",
      },
    },
    sales: {
      name: "Usuário |||| Usuários",
      fields: {
        first_name: "Nome",
        last_name: "Sobrenome",
        email: "E-mail",
        administrator: "Administrador",
        disabled: "Desativado",
      },
      create: {
        error: "Ocorreu um erro ao criar o usuário.",
        success:
          "Usuário criado. Em breve receberá um e-mail para definir a senha.",
        title: "Criar novo usuário",
      },
      edit: {
        error: "Ocorreu um erro. Tente novamente.",
        record_not_found: "Registro não encontrado",
        success: "Usuário atualizado com sucesso",
        title: "Editar %{name}",
      },
      action: {
        new: "Novo usuário",
      },
    },
    tasks: {
      name: "Tarefa |||| Tarefas",
      forcedCaseName: "Tarefa",
      fields: {
        text: "Descrição",
        due_date: "Vencimento",
        type: "Tipo",
        contact_id: "Contato",
        due_short: "vence",
      },
      action: {
        add: "Adicionar tarefa",
        create: "Criar tarefa",
        edit: "Editar tarefa",
      },
      actions: {
        postpone_next_week: "Adiar para próxima semana",
        postpone_tomorrow: "Adiar para amanhã",
        title: "ações da tarefa",
      },
      added: "Tarefa adicionada",
      deleted: "Tarefa excluída com sucesso",
      dialog: {
        create: "Criar tarefa",
        create_for: "Criar tarefa para %{name}",
      },
      sheet: {
        edit: "Editar tarefa",
        edit_for: "Editar tarefa de %{name}",
      },
      empty: "Nenhuma tarefa ainda",
      empty_list_hint:
        "Tarefas adicionadas aos seus contatos aparecem aqui.",
      filters: {
        later: "Mais tarde",
        overdue: "Atrasadas",
        this_week: "Esta semana",
        today: "Hoje",
        tomorrow: "Amanhã",
        with_pending: "Com tarefas pendentes",
      },
      regarding_contact: "(Sobre: %{name})",
      updated: "Tarefa atualizada",
    },
    tags: {
      name: "Etiqueta |||| Etiquetas",
      action: {
        add: "Adicionar etiqueta",
        create: "Criar nova etiqueta",
      },
      dialog: {
        color: "Cor",
        create_title: "Criar nova etiqueta",
        edit_title: "Editar etiqueta",
        name_label: "Nome da etiqueta",
        name_placeholder: "Digite o nome da etiqueta",
      },
    },
    leads: {
      name: "Lead |||| Leads",
      forcedCaseName: "Lead",
      fields: {
        status: "Status",
        company_id: "Empresa",
        contact_id: "Contato",
        deal_id: "Oportunidade",
        form_submission_id: "Submissão do formulário",
        service_interest_id: "Serviço de interesse",
        main_pain: "Dor principal",
        initial_message: "Mensagem inicial",
        temperature: "Temperatura",
        source: "Origem",
        environment: "Ambiente",
        is_test: "Registro de teste",
        created_at: "Criado em",
        updated_at: "Atualizado em",
      },
      empty: {
        title: "Nenhum lead encontrado",
        description: "Novos leads do formulário do site aparecem aqui.",
      },
      action: {
        new: "Novo lead",
        edit: "Editar lead",
        show: "Ver lead",
      },
    },
    form_submissions: {
      name: "Submissão |||| Submissões",
      forcedCaseName: "Submissão",
      fields: {
        status: "Status",
        source: "Origem",
        form_name: "Nome do formulário",
        conversion_event: "Evento de conversão",
        environment: "Ambiente",
        is_test: "Registro de teste",
        page_url: "URL da página",
        referrer_url: "URL de referência",
        landing_page_url: "URL de aterrissagem",
        user_agent: "User agent",
        ip_hash: "Hash do IP",
        honeypot_triggered: "Honeypot acionado",
        honeypot_fields: "Campos do honeypot",
        processing_error: "Erro de processamento",
        processed_at: "Processado em",
        created_at: "Criado em",
        raw_payload: "Payload bruto",
      },
      empty: {
        title: "Nenhuma submissão ainda",
        description: "Submissões do formulário do site são listadas aqui.",
      },
    },
    interactions: {
      name: "Interação |||| Interações",
      forcedCaseName: "Interação",
      fields: {
        type: "Tipo",
        direction: "Direção",
        channel: "Canal",
        title: "Título",
        content: "Conteúdo",
        occurred_at: "Quando ocorreu",
        company_id: "Empresa",
        contact_id: "Contato",
        lead_id: "Lead",
        deal_id: "Oportunidade",
        task_id: "Tarefa",
        source: "Origem",
        is_test: "Registro de teste",
      },
      empty: {
        title: "Nenhuma interação registrada",
        description: "Anotações, e-mails e ligações de leads e oportunidades aparecem aqui.",
      },
    },
    automation_runs: {
      name: "Execução de automação |||| Execuções de automação",
      forcedCaseName: "Execução",
      fields: {
        automation_key: "Chave da automação",
        automation_name: "Nome da automação",
        trigger_type: "Tipo do gatilho",
        trigger_source: "Origem do gatilho",
        status: "Status",
        started_at: "Iniciado em",
        finished_at: "Finalizado em",
        duration_ms: "Duração (ms)",
        error_message: "Erro",
        form_submission_id: "Submissão",
        lead_id: "Lead",
        deal_id: "Oportunidade",
        company_id: "Empresa",
        contact_id: "Contato",
      },
      empty: {
        title: "Nenhuma execução de automação",
        description: "Execuções de automação e seus status aparecem aqui.",
      },
    },
    automation_run_logs: {
      name: "Log de automação |||| Logs de automação",
      forcedCaseName: "Log",
      fields: {
        step_key: "Etapa",
        step_name: "Nome da etapa",
        status: "Status",
        message: "Mensagem",
        error_message: "Erro",
        created_at: "Criado em",
      },
    },
  },
  crm: {
    action: {
      reset_password: "Redefinir senha",
    },
    auth: {
      first_name: "Nome",
      last_name: "Sobrenome",
      confirm_password: "Confirmar senha",
      confirmation_required:
        "Acesse o link enviado por e-mail para confirmar sua conta.",
      recovery_email_sent:
        "Se você for um usuário cadastrado, deve receber em breve um e-mail para recuperar sua senha.",
      sign_in_failed: "Falha ao entrar.",
      sign_in_google_workspace: "Entrar com Google Workspace",
      signup: {
        create_account: "Criar conta",
        create_first_user:
          "Crie a primeira conta de usuário para concluir a configuração.",
        creating: "Criando...",
        initial_user_created: "Usuário inicial criado com sucesso",
      },
      welcome_title: "Bem-vindo ao CRM FaroMarket",
    },
    common: {
      activity: "Atividade",
      added: "adicionou",
      details: "Detalhes",
      last_activity_with_date: "última atividade %{date}",
      load_more: "Carregar mais",
      misc: "Diversos",
      past: "Passado",
      read_more: "Ler mais",
      retry: "Tentar novamente",
      show_less: "Exibir menos",
      copied: "Copiado!",
      copy: "Copiar",
      loading: "Carregando...",
      me: "Eu",
      task_count: "%{smart_count} tarefa |||| %{smart_count} tarefas",
    },
    changelog: {
      title: "Histórico de alterações",
    },
    activity: {
      added_company: "%{name} adicionou a empresa",
      you_added_company: "Você adicionou a empresa",
      added_contact: "%{name} adicionou",
      you_added_contact: "Você adicionou",
      added_note: "%{name} adicionou uma anotação sobre",
      you_added_note: "Você adicionou uma anotação sobre",
      added_note_about_deal:
        "%{name} adicionou uma anotação sobre a oportunidade",
      you_added_note_about_deal:
        "Você adicionou uma anotação sobre a oportunidade",
      added_deal: "%{name} adicionou a oportunidade",
      you_added_deal: "Você adicionou a oportunidade",
      at_company: "em",
      to: "para",
      load_more: "Carregar mais atividades",
    },
    dashboard: {
      deals_chart: "Receita prevista de oportunidades",
      deals_pipeline: "Funil de oportunidades",
      deal_status_won: "Ganho",
      deal_status_lost: "Perdido",
      latest_activity: "Últimas atividades",
      latest_activity_error: "Erro ao carregar últimas atividades",
      latest_notes: "Minhas últimas anotações",
      latest_notes_added_ago: "adicionada %{timeAgo}",
      stepper: {
        install: "Instalar o CRM FaroMarket",
        progress: "%{step}/3 concluído",
        whats_next: "Próximo passo?",
      },
      upcoming_tasks: "Próximas tarefas",
    },
    header: {
      import_data: "Importar dados",
    },
    image_editor: {
      change: "Alterar",
      drop_hint:
        "Solte um arquivo para enviar ou clique para selecionar.",
      editable_content: "Conteúdo editável",
      title: "Enviar e redimensionar imagem",
      update_image: "Atualizar imagem",
    },
    import: {
      action: {
        download_error_report: "Baixar relatório de erros",
        import: "Importar",
        import_another: "Importar outro arquivo",
      },
      error: {
        unable: "Não foi possível importar este arquivo.",
      },
      idle: {
        description_1:
          "Você pode importar usuários, empresas, contatos, anotações e tarefas.",
        description_2:
          "Os dados precisam estar em um arquivo JSON no formato do exemplo:",
      },
      status: {
        all_success: "Todos os registros foram importados com sucesso.",
        complete: "Importação concluída.",
        failed: "Falhou",
        imported: "Importados",
        in_progress:
          "Importação em andamento — não saia desta página.",
        some_failed: "Alguns registros não foram importados.",
        table_caption: "Status da importação",
      },
      title: "Importar dados",
    },
    settings: {
      about: "Sobre",
      companies: {
        sectors: "Setores",
      },
      dark_mode_logo: "Logo modo escuro",
      deals: {
        categories: "Categorias",
        currency: "Moeda",
        pipeline_help:
          "Selecione quais etapas devem contar como oportunidades em andamento no funil.",
        pipeline_statuses: "Status do funil",
        stages: "Etapas",
      },
      light_mode_logo: "Logo modo claro",
      notes: {
        statuses: "Status",
      },
      reset_defaults: "Restaurar padrões",
      save_error: "Falha ao salvar a configuração",
      saved: "Configuração salva com sucesso",
      saving: "Salvando...",
      tasks: {
        types: "Tipos",
      },
      preferences: "Preferências",
      title: "Configurações",
      app_title: "Título do app",
      sections: {
        branding: "Identidade visual",
        pipelines: "Funis de oportunidade",
      },
      pipelines: {
        intro:
          "Os funis e etapas do CRM ficam armazenados no banco. Esta lista mostra o estado atual.",
        loading: "Carregando funis e etapas…",
        editor_coming_soon:
          "Editor completo (rótulos, ordem, ganho/perdido, ativo/inativo) será liberado na próxima atualização.",
        readonly_badge: "Somente leitura",
        empty_fallback:
          "Não foi possível carregar os funis em tempo real. Exibindo a estrutura padrão FaroMarket abaixo.",
        error_fallback:
          "Ocorreu um problema ao renderizar os funis em tempo real. Exibindo a estrutura padrão FaroMarket abaixo.",
      },
      validation: {
        duplicate: "Duplicado %{display_name}: %{items}",
        in_use:
          "Não é possível remover %{display_name} que ainda são usados em oportunidades: %{items}",
        validating: "Validando…",
        entities: {
          categories: "categorias",
          stages: "etapas",
        },
      },
    },
    theme: {
      dark: "Escuro",
      label: "Tema",
      light: "Claro",
      system: "Sistema",
    },
    language: "Idioma",
    navigation: {
      label: "Navegação do CRM",
    },
    profile: {
      inbound: {
        description:
          "Você pode começar a enviar e-mails para o endereço de entrada do servidor (ex.: adicionando-o ao campo %{field}). O CRM processará os e-mails e adicionará anotações aos contatos correspondentes.",
        title: "E-mail de entrada",
      },
      mcp: {
        title: "Servidor MCP",
        description:
          "Use esta URL para conectar seu assistente de IA aos dados do CRM via Model Context Protocol (MCP).",
      },
      password: {
        change: "Alterar senha",
      },
      password_reset_sent:
        "Um e-mail de redefinição de senha foi enviado para o seu endereço",
      record_not_found: "Registro não encontrado",
      title: "Perfil",
      updated: "Seu perfil foi atualizado",
      update_error: "Ocorreu um erro. Tente novamente",
    },
    validation: {
      invalid_url: "Precisa ser uma URL válida",
      invalid_linkedin_url: "A URL precisa ser do linkedin.com",
    },
    faromarket: {
      panel: {
        title: "Painel operacional",
        new_leads: "Leads novos",
        pending_submissions: "Submissões pendentes",
        failed_submissions: "Submissões com erro",
        open_deals: "Oportunidades abertas",
        pending_tasks: "Tarefas pendentes",
        leads_by_source: "Leads por origem",
        leads_by_service: "Leads por serviço",
        empty_metric: "Sem dados",
        excluding_test: "Excluindo registros de teste",
      },
      lead_status: {
        new: "Novo",
        pending_required_data: "Aguardando dados",
        qualified: "Qualificado",
        disqualified: "Desqualificado",
        converted: "Convertido",
        archived: "Arquivado",
      },
      submission_status: {
        received: "Recebida",
        pending_required_data: "Aguardando dados",
        spam_suspected: "Suspeita de spam",
        processed: "Processada",
        processing_failed: "Erro no processamento",
        ignored: "Ignorada",
      },
      deal_status: {
        open: "Em aberto",
        won: "Ganha",
        lost: "Perdida",
        paused: "Pausada",
        future_nurture: "Nutrição futura",
      },
      temperature: {
        cold: "Frio",
        warm: "Morno",
        hot: "Quente",
      },
      automation_status: {
        pending: "Pendente",
        running: "Em execução",
        success: "Sucesso",
        failed: "Falha",
        skipped: "Ignorada",
        partial_success: "Sucesso parcial",
      },
      preferred_channel: {
        whatsapp: "WhatsApp",
        email: "E-mail",
        phone: "Telefone",
        other: "Outro",
      },
      direction: {
        inbound: "Entrada",
        outbound: "Saída",
        internal: "Interna",
        system: "Sistema",
      },
      environment: {
        production: "Produção",
        staging: "Homologação",
        test: "Teste",
        audit: "Auditoria",
      },
      task_type: {
        triage: "Triagem",
        follow_up: "Follow-up",
        proposal: "Proposta",
        meeting: "Reunião",
        call: "Ligação",
        email: "E-mail",
        reactivation: "Reativação",
        internal: "Interna",
      },
      task_priority: {
        low: "Baixa",
        medium: "Média",
        high: "Alta",
        urgent: "Urgente",
      },
      common: {
        yes: "Sim",
        no: "Não",
        none: "—",
      },
    },
  },
};
